import type { CaptureMeta } from '../capture/meta';

// IndexedDB del recorder: i casi registrati (fotogramma come Blob, hash, richiesta, verità, metadati di ripresa) finché non si esportano.
// Niente libreria: tre operazioni, promesse a mano.

export interface RecordedCase {
  id: string;
  blob: Blob;
  sha256: string;
  request: Record<string, unknown>;
  truth: Record<string, unknown> | null;
  /** Distanza, altezza della fotocamera, etichetta, film, inclinazione (formato 2.1). Assente nei casi registrati prima del WP1. */
  capture?: CaptureMeta | null;
  /** La foto ferma a piena risoluzione dello stesso scatto (formato 2.2), se il browser l'ha data. */
  photo?: Blob | null;
  savedAtUtc: string;
}

/**
 * Versione dell'export. 2.0 (ADR-026): la verità porta `frontMm`/`depthMm`. 2.1 (WP1): ogni caso porta il blocco `capture`.
 * Deve essere una versione che `BundleFiles` di Dim.Tools sa leggere (2.0, 2.1, 2.2). Il recorder esporta ancora la 2.1: la 2.2
 * aggiunge la foto nativa (`photo`) e arriva col passo 4 del WP1, quando questa costante passerà a '2.2'.
 */
export const EXPORT_SCHEMA_VERSION = '2.1';

/**
 * I casi registrati senza i metadati di ripresa (prima del WP1): esportarli come 2.1 sarebbe dichiarare dati che non ci sono.
 * L'export li rifiuta e dice quali sono: nella campagna ogni caso porta i suoi.
 */
export function missingCaptureIds(cases: RecordedCase[]): string[] {
  return cases.filter((c) => !c.capture).map((c) => c.id);
}

/**
 * I casi registrati con la verità della versione 1.0 (`widthMm` = fronte, `lengthMm` = profondità). Esportarli con i nomi
 * nuovi non darebbe nessun errore e scambierebbe gli assi: l'export li rifiuta e dice quali sono.
 */
export function legacyTruthIds(cases: RecordedCase[]): string[] {
  return cases.filter((c) => c.truth !== null && ('widthMm' in c.truth || 'lengthMm' in c.truth)).map((c) => c.id);
}

const DB = 'dim-recorder';
const STORE = 'cases';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
      }),
  );
}

export const recorderDb = {
  put: (c: RecordedCase): Promise<IDBValidKey> => tx('readwrite', (s) => s.put(c)),
  all: (): Promise<RecordedCase[]> => tx('readonly', (s) => s.getAll() as IDBRequest<RecordedCase[]>),
  remove: (id: string): Promise<undefined> => tx('readwrite', (s) => s.delete(id)),
  clear: (): Promise<undefined> => tx('readwrite', (s) => s.clear()),
};
